import { Corners } from "../generator/components/image-types";
import { Appearance } from "../generator/components/button-appearance";
import { ArrayScreenFormat } from "../generator/description";
import { WireComponentKind } from "./component-kind";

export interface WireAction {
    readonly token: string;
    readonly url?: string;
}

export interface WireButtonComponent {
    readonly kind: WireComponentKind.Button;
    readonly title?: string;
    readonly description?: string;
    readonly appearance: Appearance;
    readonly action: WireAction;
}

export interface WireHintComponent {
    readonly kind: WireComponentKind.Hint;
    readonly description?: string;
    readonly actionTitle?: string;
    readonly action?: WireAction;
}

export interface WireEditableValue<T> {
    readonly value: T;
    readonly onChangeToken: string;
}

export interface WireFieldComponentBase {
    readonly placeholder: string;
    readonly title: string;
}

interface WireTextFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.TextField;
    readonly value: WireEditableValue<string>;
}

interface WireNumberFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.NumberField;
    readonly value: WireEditableValue<number | undefined>;
}

interface WireEmailFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.EmailField;
    readonly value: WireEditableValue<string>;
}

interface WirePhoneFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.PhoneField;
    readonly value: WireEditableValue<string>;
}

export enum HeroStyle {
    Card = "Card",
    Accent = "Accent",
    Dark = "Dark",
    White = "White",
    Image = "Image",
    None = "None",
}

export enum HeroImageEffect {
    None = "None",
    Darken = "Darken",
    Lighten = "Lighten",
}

export enum HeroSize {
    Small = "Small",
    Medium = "Medium",
    Large = "Large",
    XLarge = "X-Large",
}

export enum DefaultImage {
    None = "None",
    Avatar = "Avatar",
    Icon = "Icon",
}

export enum SeparatorSize {
    Small = "Small",
    Medium = "Medium",
    Large = "Large",
}

interface WireActionWithTitle {
    readonly title?: string;
    readonly action?: WireAction;
}

export interface WireHeroComponent {
    readonly kind: WireComponentKind.Hero;
    readonly title?: string;
    readonly titleEmphasized?: string;
    readonly subtitle?: string;
    readonly backgroundImage?: string;
    readonly backgroundEffect: HeroImageEffect;
    readonly withBlur: boolean;
    readonly style: HeroStyle;
    readonly size: HeroSize;

    readonly image?: string;
    readonly imageCorners: Corners;

    readonly actionTitle?: string;
    readonly action?: WireAction;

    readonly secondaryActionTitle?: string;
    readonly secondaryAction?: WireAction;

    readonly menuActions?: readonly WireActionWithTitle[];
}

interface WireUserProfileComponent {
    readonly kind: WireComponentKind.UserProfile;
    readonly title?: string;
    readonly subtitle?: string;
    readonly image?: string;
}

export interface WireContactFormComponent {
    readonly kind: WireComponentKind.ContactForm;
    readonly title?: string;
    readonly subtitle?: string;
    readonly image?: string;
    readonly contactAddress?: string;
    readonly contactEmail?: string;
    readonly contactPhone?: string;

    readonly name?: WireEditableValue<string>;
    readonly email?: WireEditableValue<string>;
    readonly phone?: WireEditableValue<string>;
    readonly message?: WireEditableValue<string>;

    readonly submitAction: WireAction;
}

export enum UIAlignment {
    Start = "start",
    Center = "center",
    End = "end",
    Stretch = "stretch",
}

export enum ButtonBarSize {
    Auto = "auto",
    Wide = "wide",
}

export enum ButtonBarBackground {
    Card = "card",
    Soft = "soft",
    Accent = "accent",
    None = "none",
}

export interface WireDynamicButtonBar {
    readonly kind: WireComponentKind.DynamicButtonBar;
    readonly alignment: UIAlignment;
    readonly title?: string;
    readonly description?: string;
    readonly background: ButtonBarBackground;
    readonly size: ButtonBarSize;
    readonly buttons: readonly {
        readonly title?: string;
        // Don't render the button if the action is missing
        readonly action?: WireAction;
    }[];
}

export interface WireListListItem {
    readonly title?: string;
    readonly subtitle?: string;
    readonly caption?: string;
    readonly imageURL?: string;
    readonly action?: WireAction;
}

export interface WireCardsCollectionListItem {
    readonly title?: string;
    readonly subtitle?: string;
    readonly emphasis?: string;
    readonly body?: string;
    readonly image?: string;
    readonly heroImage?: string;

    readonly action?: WireAction;

    readonly secondaryActionTitle?: string;
    readonly secondaryAction?: WireAction;

    readonly tertiaryActionTitle?: string;
    readonly tertiaryAction?: WireAction;
}

interface WirePaging {
    readonly pageSize: number;
    readonly numPages: number;
    readonly pageIndex: WireEditableValue<number>;
}

// ignoring groups for now
interface WireListComponentGeneric<TFormat extends ArrayScreenFormat, TItem> {
    readonly kind: WireComponentKind.List;
    readonly format: TFormat;

    readonly componentTitle: string;
    readonly paging?: WirePaging;

    readonly searchBar?: WireEditableValue<string>;

    readonly dynamicFilterNames?: readonly string[];
    readonly dynamicFilterCurrentIndex?: WireEditableValue<number>;

    readonly componentActionTitle?: string;
    readonly componentAction?: WireAction;

    readonly items: readonly TItem[];
}

export enum CardStyle {
    Card = "card",
    List = "list",
    Table = "table",
    Article = "article",
}

type WireListCardCollection = WireListComponentGeneric<
    ArrayScreenFormat.CardCollection,
    WireCardsCollectionListItem
> & {
    readonly cardStyle: CardStyle;
};

export type WireDataGridColumnKind = "string" | "boolean" | "image-uri";

export interface WireDataGridColumn {
    readonly title: string;
    readonly kind: WireDataGridColumnKind;
}

export interface WireDataGridListComponent
    extends WireListComponentGeneric<ArrayScreenFormat.DataGrid, readonly (string | boolean)[]> {
    readonly columns: readonly WireDataGridColumn[];
}

export type WireListComponent =
    | WireListComponentGeneric<ArrayScreenFormat.List, WireListListItem>
    | WireListCardCollection
    | WireDataGridListComponent;

export type WireListComponentFormat = WireListComponent["format"];

export enum ContainerStyle {
    Default = "Default",
    Highlight = "Highlight",
    Accent = "Accent",
}

export interface WireSectionComponent {
    readonly kind: WireComponentKind.Container;
    readonly containerStyle: ContainerStyle;
    readonly components: readonly WireComponent[];
}

interface WireField {
    readonly name?: string;
    readonly value?: string;
}

export interface WireFieldsComponent {
    readonly kind: WireComponentKind.Fields;
    readonly title?: string;
    readonly fields: readonly WireField[];
}

export interface WireSeparatorComponent {
    readonly kind: WireComponentKind.Separator;
    readonly size: SeparatorSize;
    readonly drawLine: boolean;
}

export interface WireBreadcrumbsComponent {
    readonly kind: WireComponentKind.Breadcrumbs;
    readonly currentPageTitle?: string;
    readonly backAction?: WireAction;
    readonly crumbs: readonly WireActionWithTitle[];
}

export type WireComponent =
    | WireHeroComponent
    | WireUserProfileComponent
    | WireButtonComponent
    | WireHintComponent
    | WireTextFieldComponent
    | WireNumberFieldComponent
    | WireEmailFieldComponent
    | WirePhoneFieldComponent
    | WireContactFormComponent
    | WireDynamicButtonBar
    | WireBreadcrumbsComponent
    | WireListComponent
    | WireSectionComponent
    | WireSeparatorComponent
    | WireFieldsComponent;

export interface WireScreen {
    readonly title: string;
    // We're using `null` in the wire types because we want them to be JSON,
    // which doesn't have `undefined`.
    readonly components: readonly (WireComponent | null)[];
    readonly closeAction?: WireAction;
}

export interface WireTab {
    readonly title: string;
    readonly icon: string;
    readonly isActive: boolean;
    readonly action: WireAction;
}

export interface WireUserProfileButton {
    readonly signedInUserEmail: string;

    // If the user profile is not configured, there won't be a name or an
    // action.
    readonly userProfileAction?: WireAction;
    readonly signedInUserName?: string;
}

export interface WireUserProfile {
    // At most one of these two will be defined.  If `signInAction` is
    // defined, the user isn't signed in, and a sign-in button should show
    // that calls this action. If `userProfileButton` is defined, it gives the
    // signed-in user and an action to open the user profile screen.
    readonly signInAction?: WireAction;
    readonly userProfileButton?: WireUserProfileButton;
}

export interface WireNavigationModel extends WireUserProfile {
    readonly urlPath: string;

    readonly tabs: readonly WireTab[];
    readonly flyoutItems: readonly WireTab[];

    readonly screen: WireScreen;
    readonly sideBar?: WireScreen;
    readonly modal?: WireScreen;

    readonly removeBranding: boolean;
}
